import React from 'react';

export interface HeartsQueenOfSpadesPortraitProps {
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

/**
 * Regal African Queen of Spades Portrait
 *
 * Designed exclusively for the Hearts card game, where the Queen of Spades (the "Black Maria" / "Calamity Jane")
 * carries the singular 13-point penalty or shooting-the-moon payload.
 *
 * Features:
 * - Single regal African queen portrait (not mirrored)
 * - Deep midnight/royal indigo aura
 * - Ornate gold crown adorned with obsidian spades
 * - Braided hair with golden beads
 * - Royal indigo velvet robe and embroidered gold geometric placket
 * - Royal spade medallion necklace
 * - Hand holding the Golden Sceptre of Spades
 */
export const HeartsQueenOfSpadesPortrait: React.FC<HeartsQueenOfSpadesPortraitProps> = ({ size = 'md' }) => {
  const dims = {
    xs: 'w-6 h-8',
    sm: 'w-12 h-16 sm:w-14 sm:h-18',
    md: 'w-16 h-22 sm:w-18 sm:h-24 md:w-20 md:h-28',
    lg: 'w-20 h-28 sm:w-24 sm:h-34',
  }[size];

  return (
    <div
      data-testid="queen-of-spades-portrait"
      className={`${dims} relative flex items-center justify-center pointer-events-none select-none`}
    >
      <svg
        viewBox="0 0 100 130"
        className="w-full h-full drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="qosGoldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fef08a" />
            <stop offset="40%" stopColor="#f59e0b" />
            <stop offset="85%" stopColor="#b45309" />
            <stop offset="100%" stopColor="#78350f" />
          </linearGradient>
          <linearGradient id="qosSkinGrad" x1="20%" y1="0%" x2="80%" y2="100%">
            <stop offset="0%" stopColor="#6e3b19" />
            <stop offset="45%" stopColor="#522a10" />
            <stop offset="100%" stopColor="#381b09" />
          </linearGradient>
          <linearGradient id="qosSkinHighlight" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#8c4e23" />
            <stop offset="50%" stopColor="#a35f2d" />
            <stop offset="100%" stopColor="#8c4e23" />
          </linearGradient>
          <linearGradient id="qosRobeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4338ca" />
            <stop offset="40%" stopColor="#312e81" />
            <stop offset="80%" stopColor="#1e1b4b" />
            <stop offset="100%" stopColor="#0f172a" />
          </linearGradient>
          <linearGradient id="qosBackdropGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#0f172a" />
            <stop offset="50%" stopColor="#1e1b4b" />
            <stop offset="100%" stopColor="#090d16" />
          </linearGradient>
          <radialGradient id="qosAura" cx="50%" cy="40%" r="50%">
            <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.35" />
            <stop offset="60%" stopColor="#b45309" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#0f172a" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Outer Frame with Rounded Corners & Gold Border */}
        <rect
          x="3"
          y="3"
          width="94"
          height="124"
          rx="6"
          fill="url(#qosBackdropGrad)"
          stroke="url(#qosGoldGrad)"
          strokeWidth="1.5"
        />

        {/* Inner Subtle Ornate Border Inset */}
        <rect
          x="6.5"
          y="6.5"
          width="87"
          height="117"
          rx="4.5"
          stroke="url(#qosGoldGrad)"
          strokeWidth="0.6"
          strokeDasharray="3 1.5"
          opacity="0.85"
        />

        {/* Ambient Regal Golden Glow Aura Behind Queen */}
        <circle cx="50" cy="52" r="38" fill="url(#qosAura)" />

        {/* SINGLE REGAL AFRICAN QUEEN PORTRAIT (Full vertical framing) */}
        <g id="regal-african-queen-portrait">
          {/* Royal Ermine Patterned Collar & Robe Base */}
          <path
            d="M 16,124 Q 18,88 32,80 L 50,92 L 68,80 Q 82,88 84,124 Z"
            fill="url(#qosRobeGrad)"
            stroke="url(#qosGoldGrad)"
            strokeWidth="1"
          />

          {/* Ermine Collar / Lapels */}
          <path
            d="M 32,80 Q 50,102 68,80 Q 58,114 42,114 Q 28,112 32,80 Z"
            fill="#f8fafc"
            stroke="#cbd5e1"
            strokeWidth="0.8"
          />
          {/* Ermine Collar Tails (Spade-like black tufts) */}
          <path d="M 43,94 L 45,98 L 41,98 Z" fill="#0f172a" />
          <path d="M 50,99 L 52,103 L 48,103 Z" fill="#0f172a" />
          <path d="M 57,94 L 59,98 L 55,98 Z" fill="#0f172a" />

          {/* Queen's Neck */}
          <path
            d="M 43,62 L 43,84 Q 50,88 57,84 L 57,62 Z"
            fill="url(#qosSkinGrad)"
          />

          {/* Ornate Gold Choker / African Neck Rings */}
          <rect x="42" y="66" width="16" height="2" rx="1" fill="url(#qosGoldGrad)" />
          <rect x="41" y="70" width="18" height="2" rx="1" fill="url(#qosGoldGrad)" />
          <rect x="40" y="74" width="20" height="2.2" rx="1" fill="url(#qosGoldGrad)" />
          <rect x="39" y="78" width="22" height="2.5" rx="1" fill="url(#qosGoldGrad)" />

          {/* Queen's Head / Jaw / Face (Graceful regal oval) */}
          <path
            d="M 34,44 Q 32,66 50,71 Q 68,66 66,44 Q 65,30 50,29 Q 35,30 34,44 Z"
            fill="url(#qosSkinGrad)"
          />
          {/* Subtle Cheek Highlight */}
          <ellipse cx="50" cy="50" rx="12" ry="14" fill="url(#qosSkinHighlight)" opacity="0.45" />

          {/* Crown & Braided Coiffure Base */}
          {/* Braided Crown Volume Behind / Framing Face */}
          <path
            d="M 30,42 Q 28,24 50,22 Q 72,24 70,42 Q 68,52 68,60 Q 64,62 60,60 Q 64,48 64,38 Q 50,33 36,38 Q 36,48 40,60 Q 36,62 32,60 Q 32,52 30,42 Z"
            fill="#17120e"
          />

          {/* Gold Beads in Hair */}
          <circle cx="34" cy="46" r="1.5" fill="url(#qosGoldGrad)" />
          <circle cx="35" cy="53" r="1.5" fill="url(#qosGoldGrad)" />
          <circle cx="66" cy="46" r="1.5" fill="url(#qosGoldGrad)" />
          <circle cx="65" cy="53" r="1.5" fill="url(#qosGoldGrad)" />

          {/* Queen's Facial Features */}
          {/* Almond Eyes with Rich Regal Depth */}
          <ellipse cx="44" cy="48" rx="3.2" ry="1.8" fill="#f8fafc" />
          <circle cx="44.2" cy="48" r="1.4" fill="#0f172a" />
          <circle cx="44.8" cy="47.6" r="0.4" fill="#ffffff" />
          <path d="M 40,46 Q 44,43.5 48,46" stroke="#1f130b" strokeWidth="1.2" strokeLinecap="round" />

          <ellipse cx="56" cy="48" rx="3.2" ry="1.8" fill="#f8fafc" />
          <circle cx="55.8" cy="48" r="1.4" fill="#0f172a" />
          <circle cx="56.4" cy="47.6" r="0.4" fill="#ffffff" />
          <path d="M 52,46 Q 56,43.5 60,46" stroke="#1f130b" strokeWidth="1.2" strokeLinecap="round" />

          {/* Regal Sculpted Nose */}
          <path
            d="M 50,47 L 48.5,55 Q 50,56.5 51.5,55 Z"
            fill="#381b09"
            opacity="0.8"
          />

          {/* Full Luscious Lips with Deep Terracotta/Berry Tone */}
          <path
            d="M 46,60 Q 50,58.8 54,60 Q 50,64 46,60 Z"
            fill="#991b1b"
          />
          <path
            d="M 47,60 Q 50,60.8 53,60"
            stroke="#450a0a"
            strokeWidth="0.6"
          />

          {/* Golden Filigree Teardrop Earrings */}
          <path
            d="M 31,52 Q 28,57 31,61 Q 33,57 31,52 Z"
            fill="url(#qosGoldGrad)"
          />
          <circle cx="31" cy="62" r="1" fill="url(#qosGoldGrad)" />
          <path
            d="M 69,52 Q 72,57 69,61 Q 67,57 69,52 Z"
            fill="url(#qosGoldGrad)"
          />
          <circle cx="69" cy="62" r="1" fill="url(#qosGoldGrad)" />

          {/* Ornate Royal Golden Tiara / Crown of Spades */}
          {/* Crown Base Band */}
          <path
            d="M 33,28 Q 50,25 67,28 L 66,32 Q 50,29 34,32 Z"
            fill="url(#qosGoldGrad)"
            stroke="#78350f"
            strokeWidth="0.5"
          />
          {/* Crown Spikes / Filigree Peaks */}
          <polygon points="34,28 37,17 41,27" fill="url(#qosGoldGrad)" stroke="#78350f" strokeWidth="0.4" />
          <polygon points="41,27 45,13 49,26" fill="url(#qosGoldGrad)" stroke="#78350f" strokeWidth="0.4" />
          {/* Center Highest Crown Spire */}
          <polygon points="47,26 50,9 53,26" fill="url(#qosGoldGrad)" stroke="#78350f" strokeWidth="0.5" />
          <polygon points="51,26 55,13 59,27" fill="url(#qosGoldGrad)" stroke="#78350f" strokeWidth="0.4" />
          <polygon points="59,27 63,17 66,28" fill="url(#qosGoldGrad)" stroke="#78350f" strokeWidth="0.4" />

          {/* Obsidian Spade Insignia on Center Spire */}
          <circle cx="50" cy="18" r="4.2" fill="#09090b" stroke="url(#qosGoldGrad)" strokeWidth="0.8" />
          <path
            d="M 50,15 C 48.8,16.2 47.2,17.8 47.2,18.6 C 47.2,19.4 48,19.8 48.6,19.8 C 49.1,19.8 49.5,19.5 49.5,19.2 L 49.3,20.5 L 50.7,20.5 L 50.5,19.2 C 50.5,19.5 50.9,19.8 51.4,19.8 C 52,19.8 52.8,19.4 52.8,18.6 C 52.8,17.8 51.2,16.2 50,15 Z"
            fill="url(#qosGoldGrad)"
          />

          {/* Royal Embroidered Gold Geometric Placket */}
          <path
            d="M 44,86 L 56,86 L 58,122 L 42,122 Z"
            fill="#1e1b4b"
            stroke="url(#qosGoldGrad)"
            strokeWidth="1.2"
          />
          {/* Placket Embroidery Diamonds */}
          <polygon points="50,90 54,94 50,98 46,94" fill="url(#qosGoldGrad)" />
          <polygon points="50,101 54,105 50,109 46,105" fill="url(#qosGoldGrad)" />
          <polygon points="50,112 54,116 50,120 46,116" fill="url(#qosGoldGrad)" />

          {/* Center Royal Spade Medallion on Chest */}
          <circle cx="50" cy="85" r="5.5" fill="#09090b" stroke="url(#qosGoldGrad)" strokeWidth="1.4" />
          <path
            d="M 50,81 C 48.5,82.5 46.5,84.5 46.5,85.5 C 46.5,86.5 47.5,87 48.2,87 C 48.8,87 49.3,86.7 49.3,86.3 L 49.1,88 L 50.9,88 L 50.7,86.3 C 50.7,86.7 51.2,87 51.8,87 C 52.5,87 53.5,86.5 53.5,85.5 C 53.5,84.5 51.5,82.5 50,81 Z"
            fill="url(#qosGoldGrad)"
          />

          {/* Royal Golden Sceptre of Spades (Held upright on right) */}
          <line x1="77" y1="42" x2="77" y2="122" stroke="url(#qosGoldGrad)" strokeWidth="2.5" strokeLinecap="round" />
          {/* Sceptre Grip & Rings */}
          <rect x="75" y="70" width="4" height="2" fill="#78350f" rx="0.5" />
          <rect x="75" y="85" width="4" height="2" fill="#78350f" rx="0.5" />
          {/* Sceptre Hand / Forearm draped in gold */}
          <path d="M 73,88 Q 78,85 81,89 L 79,94 Q 74,93 73,88 Z" fill="url(#qosSkinGrad)" stroke="url(#qosGoldGrad)" strokeWidth="0.8" />
          {/* Sceptre Spade Headpiece */}
          <circle cx="77" cy="40" r="5" fill="#09090b" stroke="url(#qosGoldGrad)" strokeWidth="1.2" />
          <path
            d="M 77,35 C 75,37 72,40 72,41.5 C 72,43 73.5,43.5 74.5,43.5 C 75.5,43.5 76,43 76,42.5 L 75.5,45 L 78.5,45 L 78,42.5 C 78,43 78.5,43.5 79.5,43.5 C 80.5,43.5 82,43 82,41.5 C 82,40 79,37 77,35 Z"
            fill="url(#qosGoldGrad)"
          />
        </g>
      </svg>
    </div>
  );
};
